<template>
  <div>
    <!-- Loading -->
    <div v-if="store.isLoading" class="flex justify-center py-16">
      <Loader2 class="w-5 h-5 animate-spin text-gray-400 mr-2" />
      <span class="text-sm text-gray-500">Loading pods...</span>
    </div>

    <template v-else>
      <!-- Mobile Card View -->
      <div class="md:hidden space-y-4">
        <div
          v-for="pod in store.pods"
          :key="pod.id"
          @click="navigateToPod(pod.id)"
          class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
        >
          <div class="flex items-center justify-between mb-3">
            <div class="text-sm font-medium text-gray-900">{{ pod.name }}</div>
            <div class="text-xs text-gray-500">{{ formatDate(pod.start_date) }}</div>
          </div>

          <div class="mb-4">
            <div class="text-xs text-gray-500 mb-1">Fellows</div>
            <div class="text-sm">{{ pod.fellows?.length || 'None' }}</div>
          </div>

          <div v-if="getMeetingDisplay(pod)" class="mb-4 flex items-center gap-1 text-sm text-gray-500">
            <Clock class="w-3.5 h-3.5" />
            <span>{{ getMeetingDisplay(pod) }}</span>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex -space-x-2 overflow-hidden">
              <template v-for="fellow in pod.fellows" :key="fellow.id">
                <div
                  v-if="fellow.avatar_image"
                  class="inline-block h-8 w-8 rounded-full ring-2 ring-white overflow-hidden"
                >
                  <img
                    class="h-full w-full object-cover"
                    :class="{ 'blur-md select-none': isBlurred }"
                    :src="fellow.avatar_image"
                    :alt="`${fellow.first_name} ${fellow.last_name}`"
                  />
                </div>
                <span
                  v-else
                  class="inline-flex items-center justify-center h-8 w-8 rounded-full ring-2 ring-white bg-gray-200 overflow-hidden"
                >
                  <span
                    class="inline-flex items-center justify-center h-full w-full"
                    :class="{ 'blur-md select-none': isBlurred }"
                  >
                    <User class="h-5 w-5 text-gray-500" />
                  </span>
                </span>
              </template>
            </div>

            <div class="flex items-center">
              <component
                :is="isBlurred ? 'div' : 'router-link'"
                @click.stop
                v-if="pod.main_guide"
                :to="isBlurred ? undefined : `/profile/${pod.main_guide.username}`"
                class="relative"
              >
                <div
                  v-if="pod.main_guide.avatar_image"
                  class="inline-block h-8 w-8 rounded-full ring-2 ring-white hover:scale-105 transition-all duration-200 overflow-hidden"
                >
                  <img
                    class="h-full w-full object-cover"
                    :class="{ 'blur-md select-none': isBlurred }"
                    :src="pod.main_guide.avatar_image"
                    :alt="`${pod.main_guide.first_name} ${pod.main_guide.last_name}`"
                  />
                </div>
                <span
                  v-else
                  class="inline-flex items-center justify-center h-8 w-8 rounded-full ring-2 ring-white bg-gray-200 overflow-hidden"
                >
                  <span
                    class="inline-flex items-center justify-center h-full w-full"
                    :class="{ 'blur-md select-none': isBlurred }"
                  >
                    <User class="h-5 w-5 text-gray-500" />
                  </span>
                </span>

                <!-- Employer badges -->
                <div
                  v-if="isAdmin && getMainGuideEmployers(pod.main_guide.email).length"
                  class="absolute -bottom-1 -right-1"
                >
                  <template v-if="getMainGuideEmployers(pod.main_guide.email).length <= 2">
                    <div class="flex -space-x-1">
                      <img
                        v-for="employer in getMainGuideEmployers(pod.main_guide.email)"
                        :key="employer.name"
                        :src="employer.logo"
                        :alt="employer.name"
                        class="w-4 h-4 rounded-full border border-white bg-white object-contain hover:scale-105 transition-transform duration-150"
                        :title="employer.name"
                      />
                    </div>
                  </template>
                  <template v-else>
                    <div class="relative translate-x-0.5 translate-y-0.5">
                      <div class="flex justify-center -mb-1">
                        <img
                          :src="getMainGuideEmployers(pod.main_guide.email)[0].logo"
                          :alt="getMainGuideEmployers(pod.main_guide.email)[0].name"
                          class="w-4 h-4 rounded-full border border-white bg-white object-contain hover:scale-105 transition-transform duration-150"
                          :title="getMainGuideEmployers(pod.main_guide.email)[0].name"
                        />
                      </div>
                      <div class="flex -space-x-1">
                        <img
                          v-for="employer in getMainGuideEmployers(pod.main_guide.email).slice(1, 3)"
                          :key="employer.name"
                          :src="employer.logo"
                          :alt="employer.name"
                          class="w-4 h-4 rounded-full border border-white bg-white object-contain hover:scale-105 transition-transform duration-150"
                          :title="employer.name"
                        />
                      </div>
                    </div>
                  </template>
                </div>
              </component>
            </div>
          </div>
        </div>

        <div v-if="!store.pods.length" class="text-center py-8 text-sm text-gray-500">
          No pods found.
        </div>
      </div>

      <!-- Desktop Table View -->
      <div class="hidden md:block">
        <ManageListTable
          :items="store.pods"
          users-column-label="Fellows"
          empty-message="No pods found."
          :get-row-id="(p) => (p as Pod).id"
          :get-row-url="(p) => `/manage/pod/${(p as Pod).id}`"
          :get-display-name="(p) => (p as Pod).name"
          :get-badge="(p) => getPodBadge(p as Pod)"
          :get-users="(p) => (p as Pod).fellows ?? []"
          :get-guide="(p) => (p as Pod).main_guide ?? null"
          :get-start-date="(p) => (p as Pod).start_date"
          :get-meeting-time="(p) => getMeetingDisplay(p as Pod)"
          @row-click="(p) => navigateToPod((p as Pod).id)"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Clock, Loader2, User } from 'lucide-vue-next'
import { usePodsStore } from '@/stores/pods'
import ManageListTable from '@/components/manage/ManageListTable.vue'
import { useAuth } from '@/composables/useAuth'
import { usePrivacyMode } from '@/composables/usePrivacyMode'
import { useMainGuideEmployers } from '@/composables/useMainGuideEmployers'
import { formatDate, formatMeetingTime } from '@/utils/formatDate'
import type { Pod } from '@/types/pod'

const router = useRouter()
const store = usePodsStore()
const { isAdmin } = useAuth()
const { isBlurred } = usePrivacyMode()
const { getMainGuideEmployers } = useMainGuideEmployers()

const navigateToPod = (id: number) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(`/manage/pod/${id}`)
}

const getMeetingDisplay = (pod: Pod) => formatMeetingTime(pod.meeting_day_of_week, pod.meeting_time)

const getPodBadge = (pod: Pod) => {
  if (!pod.is_active) {
    return {
      text: 'Inactive',
      class: 'px-2 inline-flex items-center text-sm leading-5 rounded-full bg-gray-100 text-gray-500',
    }
  }
  return null
}

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  store.fetchPods()
})
</script>
